import { useCallback, useEffect, useMemo, useState } from 'react';

import { ApiOutlined, ArrowDownOutlined, ArrowUpOutlined } from '@ant-design/icons';
import { Alert, Button, Radio, Spin, Tabs } from 'antd';
import { T } from 'react-translator-component';

import CdrDetail from './CdrDetail';
import CdrsTab from './CdrsTab';
import OcpiDrawer, { DRAWER_WIDTH } from './OcpiDrawer';
import { Period, periodRange } from './ocpiHelpers';
import OverviewTab from './OverviewTab';
import PartnerFeedTab from './PartnerFeedTab';
import SessionDetail from './SessionDetail';
import SessionsTab from './SessionsTab';
import StationDetail from './StationDetail';
import StationsTab from './StationsTab';
import * as S from './styled';
import TokenTab from './TokenTab';

import { getOcpiCdrs, getOcpiLocations, getOcpiSessions, getPartnerFeedStations, PartnerFeedResult } from '../../../hooks/ocpi';
import { OcpiCdr, OcpiLocation, OcpiPage as Page, OcpiSession } from '../../../types/responses/Ocpi';

type TabKey = 'overview' | 'stations' | 'sessions' | 'cdrs' | 'received' | 'token';

export type Detail =
  | { kind: 'station'; uuid: string }
  /** A aba Recargas pagina no servidor: a recarga aberta de lá pode não estar na lista do período. */
  | { kind: 'session'; pk: number; session?: OcpiSession }
  | { kind: 'cdr'; pk: number };

export interface Navigate {
  /** Estação abre no drawer à direita, por cima da aba; recarga e CDR abrem como página. */
  open: (detail: Detail) => void;
  goTab: (tab: TabKey) => void;
  /** Estação aberta no drawer, para a lista marcar a linha. */
  station?: string;
}

/** Permissão própria: o `GET /ocpi/tokens/{uid}` do CS não tem escopo de tenant. */
const TOKEN_PERMISSION = '/ocpi/token';

const canLookupToken = (): boolean => {
  try {
    const user = JSON.parse(localStorage.getItem('auth-user') || 'null');
    return !!user?.permissions?.some(
      (permission: any) => permission.route === TOKEN_PERMISSION && !!permission.status,
    );
  } catch {
    return false;
  }
};

const EMPTY: Page<never> = { list: [], total: 0, lastPage: true };

const OcpiPage = () => {
  const [tab, setTab] = useState<TabKey>('overview');
  const [stack, setStack] = useState<Detail[]>([]);
  const [stationUuid, setStationUuid] = useState<string>();
  const [period, setPeriod] = useState<Period>('today');
  const [range, setRange] = useState(() => periodRange('today'));

  const [locations, setLocations] = useState<Page<OcpiLocation>>(EMPTY);
  const [sessions, setSessions] = useState<Page<OcpiSession>>(EMPTY);
  const [cdrs, setCdrs] = useState<Page<OcpiCdr>>(EMPTY);
  const [loadingLocations, setLoadingLocations] = useState(true);
  const [loadingPeriod, setLoadingPeriod] = useState(true);
  const [partner, setPartner] = useState<PartnerFeedResult>();

  const showToken = useMemo(canLookupToken, []);

  const loadLocations = useCallback(async () => {
    setLoadingLocations(true);
    setLocations(await getOcpiLocations());
    setLoadingLocations(false);
  }, []);

  const loadPeriod = useCallback(async () => {
    setLoadingPeriod(true);
    const next = periodRange(period);
    setRange(next);
    const [sessionPage, cdrPage] = await Promise.all([getOcpiSessions(next), getOcpiCdrs(next)]);
    setSessions(sessionPage);
    setCdrs(cdrPage);
    setLoadingPeriod(false);
  }, [period]);

  useEffect(() => {
    loadLocations();
  }, [loadLocations]);

  // Recebidas vêm do cross, não do CS: carrega em paralelo e uma vez só; falha aqui não derruba o resto.
  useEffect(() => {
    let active = true;
    getPartnerFeedStations()
      .catch((): PartnerFeedResult => ({ status: 'error' }))
      .then((result) => active && setPartner(result));
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    loadPeriod();
  }, [loadPeriod]);

  const nav: Navigate = {
    open: (detail) => {
      if (detail.kind === 'station') {
        setStationUuid(detail.uuid);
        return;
      }
      setStationUuid(undefined);
      setStack((current) => [...current, detail]);
      window.scrollTo(0, 0);
    },
    goTab: (next) => {
      setStack([]);
      setStationUuid(undefined);
      setTab(next);
    },
    station: stationUuid,
  };

  const back = () => setStack((current) => current.slice(0, -1));
  const detail = stack[stack.length - 1];

  const byUuid = useMemo(
    () => new Map(locations.list.map((location) => [location.chargeBoxUuid, location])),
    [locations.list],
  );

  const drawerLocation = stationUuid ? byUuid.get(stationUuid) : undefined;

  const failed = locations.error || sessions.error || cdrs.error;
  const showPeriod = !detail && ['overview', 'sessions', 'cdrs'].includes(tab);

  const renderDetail = () => {
    // Estação nunca entra na pilha: abre no drawer.
    if (!detail || detail.kind === 'station') return null;
    if (detail.kind === 'session') {
      const session = detail.session || sessions.list.find((s) => s.transactionPk === detail.pk);
      return session ? (
        <SessionDetail
          session={session}
          location={byUuid.get(session.chargeBoxUuid)}
          hasCdr={cdrs.list.some((c) => c.transactionPk === detail.pk)}
          nav={nav}
        />
      ) : null;
    }
    const cdr = cdrs.list.find((c) => c.transactionPk === detail.pk);
    return cdr ? <CdrDetail cdr={cdr} nav={nav} /> : null;
  };

  return (
    <S.Page>
      <S.Header>
        <div>
          <S.Title>
            <ApiOutlined style={{ marginRight: 16 }} />
            OCPI
          </S.Title>
          <S.Subtitle>{T('Stations this account sends and receives via OCPI, and the charges made on the ones it sends.')}</S.Subtitle>
        </div>
      </S.Header>

      <Tabs activeKey={tab} onChange={(key) => nav.goTab(key as TabKey)}>
        <Tabs.TabPane tab={T('Overview')} key="overview" />
        <Tabs.TabPane
          key="stations"
          tab={
            <S.TabLabel>
              <ArrowUpOutlined />
              {T('Sent stations')}
              {!loadingLocations && <span className="count">{locations.list.length}</span>}
            </S.TabLabel>
          }
        />
        <Tabs.TabPane
          key="received"
          tab={
            <S.TabLabel>
              <ArrowDownOutlined />
              {T('Received stations')}
              {partner?.status === 'ok' && <span className="count">{partner.stations.length}</span>}
            </S.TabLabel>
          }
        />
        <Tabs.TabPane tab={T('Charges')} key="sessions" />
        <Tabs.TabPane tab="CDRs" key="cdrs" />
        {showToken && <Tabs.TabPane tab={T('Look up token')} key="token" />}
      </Tabs>

      {failed && (
        <Alert
          type="error"
          showIcon
          style={{ marginBottom: 16 }}
          message={T('Could not load the OCPI data. Try again in a few minutes.')}
        />
      )}

      {detail && (
        <S.Toolbar>
          <Button onClick={back}>← {T('Back')}</Button>
        </S.Toolbar>
      )}

      {showPeriod && (
        <S.Toolbar>
          <S.Muted>{T('Period (start date)')}</S.Muted>
          <Radio.Group value={period} onChange={(e) => setPeriod(e.target.value)} buttonStyle="solid">
            <Radio.Button value="today">{T('Today')}</Radio.Button>
            <Radio.Button value="7d">{T('Last 7 days')}</Radio.Button>
            <Radio.Button value="30d">{T('Last 30 days')}</Radio.Button>
          </Radio.Group>
        </S.Toolbar>
      )}

      {loadingLocations || (showPeriod && loadingPeriod) ? (
        <S.Panel style={{ textAlign: 'center', padding: 48 }}>
          <Spin />
        </S.Panel>
      ) : (
        <>
        {detail && renderDetail()}
        {!detail && tab === 'overview' && (
          <OverviewTab
            locations={locations.list}
            sessions={sessions.list}
            cdrs={cdrs.list}
            partner={partner}
            nav={nav}
          />
        )}
        {!detail && tab === 'stations' && <StationsTab locations={locations} nav={nav} />}
        {!detail && tab === 'sessions' && <SessionsTab range={range} byUuid={byUuid} nav={nav} />}
        {!detail && tab === 'cdrs' && <CdrsTab cdrs={cdrs} nav={nav} />}
        {!detail && tab === 'received' && <PartnerFeedTab result={partner} />}
        {!detail && tab === 'token' && showToken && <TokenTab />}
        </>
      )}

      <OcpiDrawer
        width={DRAWER_WIDTH}
        visible={!!drawerLocation}
        title={drawerLocation?.chargeBoxId || ''}
        onClose={() => setStationUuid(undefined)}
      >
        {drawerLocation && (
          <StationDetail
            location={drawerLocation}
            sessions={sessions.list.filter((s) => s.chargeBoxUuid === drawerLocation.chargeBoxUuid)}
            onOpenSession={(pk) => nav.open({ kind: 'session', pk })}
          />
        )}
      </OcpiDrawer>
    </S.Page>
  );
};

export default OcpiPage;
